import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import { validateCookies } from '@/core/event_logic/final_event_composer';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { EDestinationType } from '@/core/interfaces';
import { Logger } from '@/shared/logger/logger';

import { CookieLibrary } from '../browser_storage_api/cookie_lib/cookie_lib';

export const whiteListedCookies = new Set([
  '_ga',
  '_gid',
  '_gat',
  '_gac',
  '_fbp',
  '_fbc',
  '_ttp',
  '_fbp',
  'cart',
  'checkout_session_lookup',
]);

export const whiteListedCookiesRegex = new Set([
  /^_ga/,
  /^_gid/,
  /^_gat/,
  /^_gac/,
  /^_gcl/,
]);

// eslint-disable-next-line no-constant-condition
export const queryParamsFromUrlString = (
  url: string,
): Record<string, string[]> => {
  try {
    // eslint-disable-next-line no-constant-condition
    return new URL(url)
      ? Array.from(new URL(url).searchParams.entries()).reduce<
          Record<string, string[]>
        >((acc, [key, value]) => {
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(value);
          return acc;
        }, {})
      : {};
  } catch (error) {
    Logger.warn(`Error parsing URL: ${url} ${url?.toString()}`, error);
    return {};
  }
};

/**
 * Browser Handler Object
 */
export class BrowserApi {
  static filterInvalidData(
    browserData: TBrowserApiData,
    additionalWhitelistedCookies?: string[],
  ): TBrowserApiData {
    if (browserData.cks) {
      browserData.cks = Object.fromEntries(
        Object.entries(browserData.cks).filter(
          ([key]) =>
            whiteListedCookies.has(key) ||
            // Check against regex patterns
            Array.from(whiteListedCookiesRegex).some(regex =>
              regex.test(key),
            ) ||
            additionalWhitelistedCookies?.includes(key) ||
            additionalWhitelistedCookies?.some(pattern =>
              new RegExp(pattern).test(key),
            ),
        ),
      );
    }
    return browserData;
  }
  private static _browserData: TBrowserApiData;
  private static readonly currScript =
    document.currentScript as HTMLScriptElement;

  static scriptUrl = new URL(
    BrowserApi.currScript?.src || 'http://localhost:9999/',
  );
  static documentUrl = new URL(document.URL);

  static getUrlParam = (key: string, urlSearch?: string) => {
    const params = new URLSearchParams(urlSearch || location.search);
    return params.get(key);
  };

  static init = async () => {
    const { location, referrer, title } = document;
    const href = location.href;
    const { language, userAgent } = navigator;
    const { height, width } = screen;
    const qpar: Record<string, string[]> = queryParamsFromUrlString(
      BrowserApi.documentUrl.href,
    );
    const currentTime = await TimeUtils.getCurrentEpochInSecondsSynced();
    const cks: Record<string, string> = validateCookies(
      CookieLibrary.getAll() as Record<string, string>,
      qpar,
      currentTime,
      [
        EDestinationType.Facebook,
        EDestinationType.TikTok,
        EDestinationType.GA4,
      ],
    );

    const browser_data: TBrowserApiData = {
      cks,
      dl: href,
      dt: title,
      mp: 0,
      pp: performance.now(),
      qpar,
      rl: referrer,
      srh: height,
      srw: width,
      ua: userAgent,
      ul: language,
    };

    BrowserApi._browserData = browser_data;
    return browser_data;
  };

  static set = (browser_data: TBrowserApiData) => {
    BrowserApi._browserData = browser_data;
  };

  static urlSearchParamsToObject = (
    urlSearchParams: URLSearchParams,
  ): Record<string, string> => Object.fromEntries(urlSearchParams.entries());

  static parseQueryParamsFromUrlString = (
    url: string,
  ): Record<string, string> => {
    if (!url) {
      return {};
    }
    const queryParams = new URL(url).searchParams;
    return BrowserApi.urlSearchParamsToObject(queryParams);
  };

  /**
   * Get the Session Data
   * @returns BrowserApiData
   */
  static get = async (
    additionalWhitelistedCookies?: string[],
  ): Promise<TBrowserApiData> =>
    BrowserApi.filterInvalidData(
      BrowserApi._browserData || (await BrowserApi.init()),
      additionalWhitelistedCookies,
    );

  static logScriptLoadingTime = async (scriptUrl: string) => {
    const scriptEntries = performance
      .getEntriesByType('resource')
      ?.filter(
        (entry): entry is PerformanceResourceTiming =>
          entry instanceof PerformanceResourceTiming &&
          entry.name.includes(scriptUrl),
      );

    if (
      scriptEntries.length > 0 &&
      scriptEntries[0]?.startTime &&
      scriptEntries[0].responseEnd
    ) {
      const { responseEnd, startTime } = scriptEntries[0];
      const loadingTime = (responseEnd - startTime).toFixed(2);
      Logger.info(
        `The script ${scriptUrl} took ${loadingTime} milliseconds to load.`,
      );
    } else {
      Logger.info(
        `The script ${scriptUrl} was not found in the performance entries.`,
      );
    }
  };

  static getExternalPageId = () =>
    BrowserApi.scriptUrl.searchParams.get('ext_page');
}
